import type { ReactNode } from 'react';
import { cx } from '../internal/cx';
import { devWarn } from '../internal/dev-warn';
import { brandAssets } from '../../assets';

export interface ProofSealProps {
  /**
   * Rótulo visível obrigatório — o selo "confirma, não substitui" (brandbook §9);
   * nunca aparece sozinho como indicador de estado. A imagem em si é `alt=""`
   * (decorativa) porque o rótulo já carrega o significado para leitor de tela.
   */
  label: ReactNode;
  /** Tamanho em px — mínimo 32 (brandbook §8). Abaixo disso dispara aviso de dev. */
  size?: number;
  className?: string;
}

const SEAL_MIN_SIZE = 32;

/**
 * Uso: estado verificado / conclusão de fluxo / prova disponível / validação
 * bem-sucedida. NUNCA como avatar, mascote, decoração repetitiva ou substituto do
 * símbolo A-nó (brandbook §9, tabela "Nunca use para").
 *
 * `argink-selo-prova.png` desenha o corpo da aranha em Paper/creme — é arte
 * feita para repousar sobre fundo ESCURO (só o núcleo é Proof Lime, que aí sim
 * lê em qualquer fundo). Todo consumidor atual do componente monta em cima de
 * `Card`/`body`, que são superfície Paper (clara): sem correção o corpo do selo
 * some e sobra só o núcleo lime flutuando. Não existe um PNG on-light — só há
 * este asset (`packages/ui/src/assets/index.ts`), e o mandato (§10/§12) proíbe
 * redesenhar a marca sem o SVG master. A correção possível dentro da marca é
 * dar ao selo o fundo Ink que ele já pressupõe, sempre, via um chip próprio —
 * não depende de o consumidor estar em fundo claro ou escuro porque o asset é
 * fixo para um dos dois. Pendência real, fora do escopo desta correção: pedir
 * ao brandbook uma versão on-light do selo.
 */
export function ProofSeal({ label, size = 32, className }: ProofSealProps) {
  if (size < SEAL_MIN_SIZE) {
    devWarn(
      `ProofSeal está sendo renderizado a ${size}px, abaixo do mínimo de ${SEAL_MIN_SIZE}px ` +
        '(BRAND-EXTRACT §8).',
    );
  }

  return (
    <span className={cx('argink-proof-seal', className)}>
      <span className="argink-proof-seal__chip">
        <img
          src={brandAssets.seloProva}
          alt=""
          width={size}
          height={size}
          className="argink-proof-seal__image"
        />
      </span>
      <span className="argink-proof-seal__label">{label}</span>
    </span>
  );
}
